import firebase from 'firebase/compat/app'
import moment from 'moment-timezone'
import { MessageType } from '../models/message'
import config from '../config'
import axios from 'axios'
import store from '../store'
import { ConversationsService } from '../services'
import cloneDeep from 'lodash-es/cloneDeep'

export interface ConversationMessageResponse {
  lastMessageId?: string
  messages: any[]
  nextPage: boolean
}

export enum ConversationBulkActionTypes {
  Delete = 'delete',
  MarkAsRead = 'markasread',
  Starred = 'starred',
  Archive = 'archive',
}

export enum ConversationBulkActionFilters {
  Selected = 'selected',
}

export interface IConversationBulkAction {
  locationId: string
  action: ConversationBulkActionTypes
  filter: ConversationBulkActionFilters
  value: boolean
  conversationIds: string[]
}

export interface IConversationUpdate {
  locationId: string
  unreadCount?: number
  starred?: boolean
}

export class IConversationBulkResponse {
  success: boolean
  count: number
  type?: string
}

export enum ConversationFilterType {
  TYPE_ALL = 'all',
  TYPE_READ = 'read',
  TYPE_UNREAD = 'unread',
  TYPE_STARRED = 'starred',
  TYPE_RECENTS = 'recents',
  TYPE_INTERNAL = 'internal',
}

export interface IConversationQuickFilter {
  filter: string[]
  assignedTo: string[]
  refresh?: boolean
}
export enum ConversationMessageTypes {
  SMS = 'SMS',
  Email = 'Email',
  WHATSAPP = 'WhatsApp',
  GMB = 'GMB',
  IG = 'IG',
  FB = 'FB',
  Custom = 'Custom',
  WEB_CHAT = 'WebChat',
  LIVE_CHAT = 'Live_Chat',
}

export default class Conversation {
  public static TYPE_PHONE = 1
  public static TYPE_EMAIL = 2
  public static TYPE_FB_MESSENGER = 3
  public static TYPE_REVIEW = 4
  public static TYPE_GROUP_SMS = 5

  public static collectionRef() {
    return firebase.firestore().collection('conversations')
  }
  public static getStreamById(id: string): firebase.firestore.DocumentReference {
    return Conversation.collectionRef().doc(id)
  }
  public static getAllConversations(locationId: string, inbox: boolean): firebase.firestore.Query {
    let query = Conversation.collectionRef().where('deleted', '==', false).where('location_id', '==', locationId)
    if (inbox) {
      query = query.where('inbox', '==', true)
    }
    return query.orderBy('last_message_date', 'desc')
  }
  public static getAllConversationsRealtime(locationId: string, inbox?: boolean): firebase.firestore.Query {
    let query = Conversation.collectionRef().where('deleted', '==', false).where('location_id', '==', locationId)
    if (inbox) {
      query = query.where('inbox', '==', true)
    }
    return query.orderBy('date_updated', 'desc')
  }
  public static getById(id: string): Promise<Conversation> {
    return new Promise((resolve, reject) => {
      Conversation.collectionRef()
        .doc(id)
        .get()
        .then((snapshot: firebase.firestore.DocumentSnapshot) => {
          if (snapshot.exists) resolve(new Conversation(snapshot))
          resolve()
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }
  public static getByContactId(contactId: string, locationId: string): Promise<Conversation[]> {
    return new Promise((resolve, reject) => {
      Conversation.collectionRef()
        .where('deleted', '==', false)
        .where('contact_id', '==', contactId)
        .where('location_id', '==', locationId)
        .get()
        .then(snapshot => {
          resolve(snapshot.docs.map(d => new Conversation(d)))
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }
  public static getByContactIdRealTime(contactId: string, locationId: string): firebase.firestore.Query {
    return Conversation.collectionRef()
      .where('deleted', '==', false)
      .where('contact_id', '==', contactId)
      .where('location_id', '==', locationId)
  }
  public static getByContactIdAndType(
    contactId: string,
    type: number,
    locationId: string
  ): Promise<Conversation | undefined> {
    return new Promise((resolve, reject) => {
      Conversation.collectionRef()
        .where('deleted', '==', false)
        .where('contact_id', '==', contactId)
        .where('type', '==', type)
        .where('location_id', '==', locationId)
        .get()
        .then(snapshot => {
          if (snapshot.empty) resolve()
          else if (snapshot.size > 1) reject()
          resolve(new Conversation(snapshot.docs[0]))
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }
  public static async getConversationsFromES(
    locationId: string,
    inbox: boolean,
    term: string,
    startAfterDate: undefined | number,
    assignedTo: undefined | string,
    contactId?: undefined | string,
    unread?: boolean
  ): Promise<Conversation[]> {
    let url = `${config.newBaseUrl}/search/conversation/${
      inbox ? 'inbox' : 'all'
    }?location_id=${locationId}&conversation_type=TYPE_PHONE,TYPE_REVIEW`
    if (term) {
      url += `&q=${term}`
    }
    if (startAfterDate) {
      url += `&startAfterDate=${startAfterDate}`
    }
    if (assignedTo) {
      url += `&assigned_to=${assignedTo}`
    }
    if (contactId) {
      url += `&contact_id=${contactId}`
    }

    if (unread) {
      url += `&unread=${unread}`
    }
    return axios
      .get(url)
      .then(response => {
        return response.data.hits.hits
      })
      .catch(err => {
        console.log(err)
        return err
      })
  }

  /**
   * The function to convert object into query string. Currently support single level. Also supports Arrays
   * @param obj Any object
   * @returns string
   */
  private static getQueryStringFromObject(obj: any) {
    const queryStringArr: string[] = []
    for (const key in obj) {
      if (typeof obj[key] == 'object' && Array.isArray(obj[key])) {
        for (const val of obj[key]) {
          queryStringArr.push(`${key}=${encodeURIComponent(val)}`)
        }
      } else {
        queryStringArr.push(`${key}=${encodeURIComponent(obj[key])}`)
      }
    }

    return queryStringArr.join('&')
  }

  public static async getConversationsV2(filters: any): Promise<{ conversations: Conversation[]; total: number }> {
    const sanitizedSearchBody = Object.fromEntries(
      Object.keys(filters)
        .filter(filter => {
          if (filters[filter] == null) return false
          if (typeof filters[filter] == 'string' && filters[filter].length === 0) return false
          return true
        })
        .map(filter => {
          return [filter, filters[filter]]
        })
    )
    const data: { conversations: Conversation[]; total: number } = await ConversationsService.getConversationsV2(
      this.getQueryStringFromObject(sanitizedSearchBody)
    )
    return data
  }

  public static async performBulkAction(data: IConversationBulkAction): Promise<IConversationBulkResponse> {
    const response: IConversationBulkResponse = await ConversationsService.performBulkActions(data)
    return response
  }
  public static async getConversationsByIdES(locationId: string, id: undefined | string): Promise<Conversation[]> {
    let url = `${config.newBaseUrl}/search/conversation/all?location_id=${locationId}`
    if (id) {
      url += `&id=${id}`
    }
    return axios
      .get(url)
      .then(response => {
        return response.data.hits.hits
      })
      .catch(err => {
        return err
      })
  }
  public static create(
    locationId: string,
    contactId: string,
    type: number,
    extras?: {
      channel?: string
      lastMessageBody?: string
      lastMessageType?: MessageType
      assignedTo?: string
      followers?: string[]
    }
  ): Conversation {
    const conversation = new Conversation()
    conversation.contactId = contactId
    conversation.locationId = locationId
    conversation.type = type
    if (extras && extras.channel) conversation.channel = extras.channel
    if (extras && extras.lastMessageBody) conversation.lastMessageBody = extras.lastMessageBody
    if (extras && extras.lastMessageType) {
      conversation.lastMessageType = extras.lastMessageType
      if (
        [
          MessageType.TYPE_SMS,
          MessageType.TYPE_CALL,
          MessageType.TYPE_EMAIL,
          MessageType.TYPE_WEBCHAT,
          MessageType.TYPE_FACEBOOK,
          MessageType.TYPE_LIVE_CHAT,
        ].indexOf(extras.lastMessageType) !== -1
      )
        conversation.inbox = true
    }
    if (extras && extras.assignedTo) conversation.assignedTo = extras.assignedTo
    if (extras && extras.followers) conversation.followers = extras.followers
    return conversation
  }
  public static async createAndSave(
    locationId: string,
    contactId: string,
    type: number,
    extras?: {
      channel?: string
      lastMessageBody?: string
      lastMessageType?: MessageType
      assignedTo?: string
      followers?: string[]
    }
  ): Promise<Conversation> {
    const conversation = Conversation.create(locationId, contactId, type, extras)
    await conversation.save()
    return conversation
  }
  public static getByLocationContactType(locationId: string, type: number): firebase.firestore.Query {
    return Conversation.collectionRef()
      .where('deleted', '==', false)
      .where('location_id', '==', locationId)
      .where('type', '==', type)
  }
  public static last(fn): (locationId: string, inbox: boolean, term: string) => Promise<Conversation[]> {
    const lastToken = { cancel: function () {} } // start with no op
    return function (...args) {
      lastToken.cancel()
      args.push(lastToken)
      return fn.apply(this, args)
    }
  }
  private _data: firebase.firestore.DocumentData
  private _id: string
  private _ref: firebase.firestore.DocumentReference
  private _snapshot: firebase.firestore.DocumentSnapshot
  constructor(snapshot?: firebase.firestore.DocumentSnapshot | { [key: string]: any }) {
    if (
      snapshot instanceof firebase.firestore.DocumentSnapshot ||
      snapshot instanceof firebase.firestore.QueryDocumentSnapshot
    ) {
      this._id = snapshot.id
      this._data = snapshot.data() || {}
      this._ref = snapshot.ref
      this._snapshot = snapshot
    } else if (snapshot) {
      this._id = snapshot.id
      this._data = cloneDeep(snapshot)
      this._ref = Conversation.collectionRef().doc(snapshot.id)
      this._data.date_added = new firebase.firestore.Timestamp(Math.floor(snapshot.date_added / 1000), 0)
      this._data.date_updated = new firebase.firestore.Timestamp(Math.floor(snapshot.date_updated / 1000), 0)
      this._data.last_message_date = new firebase.firestore.Timestamp(Math.floor(snapshot.last_message_date / 1000), 0)
      if (snapshot.last_inbound_whatsapp_message_date instanceof firebase.firestore.Timestamp) {
        this._data.last_inbound_whatsapp_message_date = snapshot.last_inbound_whatsapp_message_date
      } else {
        this._data.last_inbound_whatsapp_message_date = new firebase.firestore.Timestamp(
          Math.floor(snapshot.last_inbound_whatsapp_message_date / 1000),
          0
        )
      }
    } else {
      this._ref = Conversation.collectionRef().doc()
      this._id = this._ref.id
      this._data = {}
      this.deleted = false
      this.dateAdded = moment().utc()
    }
  }
  get id(): string {
    return this._id
  }
  get ref(): firebase.firestore.DocumentReference {
    return this._ref
  }
  get snapshot() {
    return this._snapshot
  }
  get data(): firebase.firestore.DocumentData {
    return this._data
  }
  get dateAdded(): moment.Moment {
    return moment(this._data.date_added.toMillis())
  }
  set dateAdded(dateAdded: moment.Moment) {
    this._data.date_added = firebase.firestore.Timestamp.fromMillis(dateAdded.valueOf())
  }
  get dateUpdated(): moment.Moment {
    return moment(this._data.date_updated.toMillis())
  }
  set dateUpdated(dateUpdated: moment.Moment) {
    this._data.date_updated = firebase.firestore.Timestamp.fromMillis(dateUpdated.valueOf())
  }
  get lastMessageDateTimestamp(): number {
    return this._data.last_message_date
  }
  get lastMessageDate(): moment.Moment {
    return moment(this._data.last_message_date.toMillis())
  }

  set lastMessageDate(lastMessageDate: moment.Moment) {
    this._data.last_message_date = firebase.firestore.Timestamp.fromMillis(lastMessageDate.valueOf())
  }

  get lastManualMessageDate(): moment.Moment {
    // return moment(this._data.last_message_date.toMillis())
    if (this._data.last_manual_message_date) {
      return this._data.last_manual_message_date === 'number' || this._data.last_manual_message_date === 'string'
        ? moment(this._data.last_manual_message_date)
        : moment(
            new firebase.firestore.Timestamp(
              this._data.last_manual_message_date.seconds,
              this._data.last_manual_message_date.nanoseconds
            ).toMillis()
          )
    }
    return undefined
  }

  set lastManualMessageDate(lastManualMessageDate: moment.Moment) {
    this._data.last_manual_message_date = firebase.firestore.Timestamp.fromMillis(lastManualMessageDate.valueOf())
  }
  get lastInboundWhatsAppMessageDate(): moment.Moment {
    return this._data.last_inbound_whatsapp_message_date
      ? moment(this._data.last_inbound_whatsapp_message_date.toMillis())
      : undefined
  }
  set lastInboundWhatsAppMessageDate(time: moment.Moment) {
    this._data.last_inbound_whatsapp_message_date = firebase.firestore.Timestamp.fromMillis(time.valueOf())
  }
  get deleted(): boolean {
    return this._data.deleted
  }
  set deleted(deleted: boolean) {
    this._data.deleted = deleted
  }
  get locationId(): string {
    return this._data.location_id
  }
  set locationId(locationId: string) {
    this._data.location_id = locationId
  }
  get contactId(): string {
    return this._data.contact_id
  }
  set contactId(contactId: string) {
    this._data.contact_id = contactId
  }
  get userId(): string {
    return this._data.user_id
  }
  set userId(userId: string) {
    this._data.user_id = userId
  }
  get assignedTo(): string {
    return this._data.assigned_to
  }
  set assignedTo(assignedTo: string) {
    this._data.assigned_to = assignedTo
  }

  get followers(): string[] {
    return this._data.followers
  }

  set followers(followers: string[]) {
    this._data.followers = followers
  }

  get type(): number {
    return this._data.type
  }
  set type(type: number) {
    this._data.type = type
  }
  get altId(): string {
    return this._data.alt_id
  }
  set altId(altId: string) {
    this._data.alt_id = altId
  }
  get channel(): string {
    return this._data.channel
  }
  set channel(channel: string) {
    this._data.channel = channel
  }
  get lastMessageBody(): string {
    return this._data.last_message_body
  }
  set lastMessageBody(lastMessageBody: string) {
    this._data.last_message_body = lastMessageBody
  }
  get lastMessageType(): MessageType {
    return this._data.last_message_type
  }
  set lastMessageType(lastMessageType: MessageType) {
    this._data.last_message_type = lastMessageType
  }
  get unreadCount(): number {
    return this._data.unread_count || 0
  }
  set unreadCount(unreadCount: number) {
    this._data.unread_count = unreadCount
  }

  get reviewId(): string {
    return this._data.review_id
  }

  set reviewId(reviewId: string) {
    this._data.review_id = reviewId
  }

  get gmbPageId(): string {
    return this._data.gmb_page_id
  }

  set gmbPageId(gmbPageId: string) {
    this._data.gmb_page_id = gmbPageId
  }

  get reviewerName(): string {
    return this._data.reviewer_name
  }

  set reviewerName(reviewerName: string) {
    this._data.reviewer_name = reviewerName
  }

  get starRating(): number {
    return this._data.star_rating
  }

  set starRating(starRating: number) {
    this._data.star_rating = starRating
  }

  get starred(): boolean {
    return this._data.starred || false
  }
  set starred(starred: boolean) {
    this._data.starred = starred
  }

  public async toggleRead() {
    if (this.unreadCount === 0) {
      this.unreadCount = 1
      this.ref.update({
        unread_count: 1,
        date_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    } else {
      this.unreadCount = 0
      store.dispatch('conversation/markAsReadRemoveFromUnreads', { conversationId: this.id })
      this.ref.update({
        unread_count: 0,
        date_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    }
  }

  public async toggleStarred() {
    // if (this.starred) {
    //   this.starred = false
    //   this.ref.update({
    //     starred: false,
    //     date_updated: firebase.firestore.FieldValue.serverTimestamp()
    //   })
    // } else {
    //   this.starred = true
    //   this.ref.update({
    //     starred: true,
    //     date_updated: firebase.firestore.FieldValue.serverTimestamp()
    //   })
    // }
    const newValue: boolean = !this.starred
    this.starred = newValue
    await ConversationsService.updateConversation(this.id, {
      starred: newValue,
      locationId: this.locationId,
    })
  }
  public async toggleArchive() {
    if (this.inbox) {
      this.inbox = false
      this.ref.update({
        inbox: false,
        date_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    } else {
      this.inbox = true
      this.ref.update({
        inbox: true,
        date_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    }
  }
  //   public async archive() {
  //     this.inbox = false
  //     await this.save()
  //   }
  // ​
  //   public async unarchive() {
  //     this.inbox = true
  //     await this.save()
  //   }
  get inbox(): boolean {
    return this._data.inbox
  }
  set inbox(inbox: boolean) {
    this._data.inbox = inbox
  }

  get aiBotChannelCounter(): object {
    return this._data.ai_bot_channel_counter
  }

  set aiBotChannelCounter(counterObject: object) {
    this._data.ai_bot_channel_counter = counterObject
  }

  get aiBotTime(): moment.Moment {
    return moment(this._data.ai_bot_Time.toMillis())
  }

  set aiBotTime(botTime: moment.Moment) {
    this._data.ai_bot_Time = firebase.firestore.Timestamp.fromMillis(botTime.valueOf())
  }

  get aiBotStatus(): string {
    return this._data.ai_bot_status
  }

  set aiBotStatus(botStatus: string) {
    this._data.ai_bot_status = botStatus
  }

  get autoReplyDueTime(): moment.Moment {
    return moment(this._data.auto_reply_due_time.toMillis())
  }
  set autoReplyDueTime(autoReplyDueTime: moment.Moment) {
    this._data.auto_reply_due_time = firebase.firestore.Timestamp.fromMillis(autoReplyDueTime.valueOf())
  }

  get startedFromTheUi(): boolean {
    return this._data.startedFromTheUi
  }

  set startedFromTheUi(startedFromTheUi: boolean) {
    this._data.startedFromTheUi = startedFromTheUi
  }

  get chatServiceSid(): string {
    return this._data.chat_service_sid
  }

  get fullName(): string {
    return this._data.full_name
  }

  public save(): Promise<Conversation> {
    const _self = this
    return new Promise((resolve, reject) => {
      _self.data.date_updated = firebase.firestore.FieldValue.serverTimestamp()
      _self._ref
        .set(this._data, { merge: true })
        .then(() => {
          resolve(_self)
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }
  public delete() {
    return Conversation.collectionRef().doc(this.id).update({
      deleted: true,
    })
  }
}
